import React, { useState, useEffect } from 'react';
import {
  Button, Col, Row, Input, Spin,
  Table, Form,
  Select,
  Modal,
  Divider,
  notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';

import './BaseYearExercises.scss';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  del, patch, RequestWrapper, ResponseWrapper, isSuperAgentError,
} from '../../dataSource/requests';

import ModalForm from './ModalForm';
import { BaseYear } from '../../sharedTypes/BaseYear';

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
  itemsInPage?: number | undefined,
}

interface IBaseYear {
  baseYear: 0,
  id: 0,
  status: string
}

const BaseYearExercises = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<IBaseYear>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [baseYearEdit, setBaseYearEdit] = useState<BaseYear | undefined>();

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'id',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;

    const values = formInstance.getFieldsValue();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'id',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: '/ir-base-year/listAll',

      params: {
        ...Object.fromEntries(
          Object
            .entries(values)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);
    }

    switch (true) {
      case (response != null && response?.body?.code === 22000): {
        // "Notification category request successfully", 22000
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        setTableDataSource(response?.body?.incomingReportOutputs);

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'id',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  const deletionRequests = async (id:number):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const requestReference: RequestWrapper = del({
      url: `/ir-base-year/${id}`,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 22002): {
        executeFilter();
        break;
      }

      case (responseError?.body?.code === -24000): {
        notification.error({
          message: 'Erro',
          description: 'Exercício/ano-base não encontrado!',
        });

        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro tentar deletar um ano base, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const showDelete = (id: number): void => {
    Modal.confirm({
      title: 'Tem certeza que deseja deletar este ano?',
      okText: 'Sim, deletar',
      okType: 'danger',
      cancelText: 'Não, cancelar',

      onOk: () => deletionRequests(id),

      onCancel: (): void => undefined,
    });
  };

  useEffect(() => {
    executeFilter();

    return () => {
      setLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="BaseYearExercises">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row
          className="BaseYearExercises__ComponentRow"
          gutter={[24, 24]}
        >
          <Col
            className="BaseYearExercises__ComponentColumn"
            xs={{ span: 24 }}
          >
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="title-container"
            >
              <Col
                className="BaseYearExercises__PageTitleColumn"
              >
                <span className="page-title">
                  Cadastro de Exercício/Ano Base
                </span>

              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                <Button
                  block
                  className="btn-add"
                  type="primary"
                  onClick={(): void => {
                    setBaseYearEdit(undefined);
                    setOpenModal(true);
                  }}
                >
                  <PlusIcon />
                  Cadastrar
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={24}
            className="filters-container"
          >

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Exercício/ano-base "
                name="baseYear"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o ano"
                />
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Status"
                name="status"
                rules={[]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Selecione o status"
                >
                  <Select.Option value="ACTIVE">
                    Ativo
                  </Select.Option>
                  <Select.Option value="INACTIVE">
                    Inativo
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 12 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-clean"
                  htmlType="reset"
                  onClick={
                  (): void => {
                    formInstance.resetFields();
                    executeFilter();
                  }
                }
                >
                  <FilterRemoveIcon />
                  Limpar
                </Button>
              </Form.Item>
            </Col>
            <Col xs={{ span: 12 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-filter"
                  type="primary"
                  htmlType="submit"
                >
                  <FilterIcon />
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="AccountDeletionRequests__Table"
                    columns={[
                      {
                        title: <div className="table-titles">ID</div>,
                        dataIndex: 'id',
                        key: 'id',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                      },
                      {
                        title: <div className="table-titles">Ano</div>,
                        key: 'baseYear',
                        dataIndex: 'baseYear',
                        ellipsis: true,
                        sorter: true,
                      },
                      {
                        title: <div className="table-titles">Status</div>,
                        dataIndex: 'status',
                        key: 'status',
                        ellipsis: true,
                        sorter: true,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.status === 'ACTIVE') {
                            return 'Ativo';
                          }
                          return 'Inativo';
                        },
                      },
                      {
                        title: <div className="table-titles">Ação</div>,
                        dataIndex: 'id',
                        key: 'acoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => (
                          <span>
                            <Button
                              icon={<EditOutlined />}
                              onClick={(): void => {
                                setBaseYearEdit(record);
                                setOpenModal(true);
                              }}
                            />
                            <Divider type="vertical" />
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={(): void => showDelete(record.id)}
                            />
                          </span>

                        ),
                        width: 170,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
      <ModalForm
        open={openModal}
        modalStatusChange={setOpenModal}
        baseYearEdit={baseYearEdit}
        updateTable={executeFilter}
      />
    </div>
  );
};

export default BaseYearExercises;
